import { fundingPFColumnJson, headerColumnGroupFundingPF } from "app/Data/fundingPFColumnJson";
import { getAgentBranding } from "app/redux/features/agent-branding";
import { useComments } from "app/uicomponents/macrocomponents/Comments/CommentContext";
import CommentLayout from "app/uicomponents/macrocomponents/Comments/CommentLayout";
import InsuredFilter from "app/uicomponents/macrocomponents/InteractiveProposal/Funding/InsuredFilter";
import InsuredTable from "app/uicomponents/macrocomponents/InteractiveProposal/Funding/InsuredTable";
import { intToString } from "app/uicomponents/macrocomponents/PolicySummary/config";
import ScreenLoader from "app/uicomponents/microcomponents/ScreenLoader";
import { makeRequest } from "app/utils/makeRequest";
import { getSessionStore } from "app/utils/sessionStore";
import {
  FundingPFFilters,
  FundingSDFilters,
  ProposalFilter,
  ProposalTypes,
  THEME,
} from "app/utils/types";
import {
  fundingOptions,
  fundingPFOptions,
  fundingSDOptions,
  getThemeColors,
  getTooltipColors,
  getUSANumberFormat,
} from "app/utils/utils";
import ReactECharts from "echarts-for-react";
import { Column } from "primereact/column";
import { Row } from "primereact/row";
import { useCallback, useRef, useState } from "react";
import { GoTriangleDown, GoTriangleUp } from "react-icons/go";
import { useSelector } from "react-redux";
import { fundingColumnJson } from "../../../Data/fundingColumnJson";
import { fundingSDColumnJson, headerColumnGroupFundingSD } from "../../../Data/fundingSDColumnJson";
import { useIPContext } from "../InteractiveProposalProvider";
import { Accordion, AccordionTab } from "primereact/accordion";
import ChartFilter from "../Common/ChartFilter";

let selectedFundingData: any = null;
const InteractiveProposalFunding = () => {
  const zoomStateRef = useRef<{ start: number; end: number } | null>(null);
  const selectedSeriesRef = useRef<string | null>(null);
  const [chartDataSets, setChartDataSets] = useState<any>(null);
  const [tableData, setTableData] = useState<any>(null);
  const [graphData, setGraphData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const { selectedVersion, summaryData: proposalSummary } = useIPContext();
  const proposalId = selectedVersion?.id ?? getSessionStore("interactiveProposalID");
  const globalTheme = getSessionStore("globalTheme");
  const { settingThemeData } = useSelector(getAgentBranding);
  const { branding } = getSessionStore("lookUpResponse");
  const { primaryColor, secondaryColor } = getThemeColors(branding);
  const { setIsCurrPageLoading } = useComments();
  const { selectedChartFilter } = useIPContext();
  const contactBranding = useSelector((state: any) => state.contactBranding);
  const planInfo = getSessionStore("planInfo");
  const brandingFromPlanInfo = planInfo?.branding;
  const isDarkMode = globalTheme === THEME.DARK;
  // Use branding from planInfo if available, otherwise use Redux state
  const finalContactBranding = brandingFromPlanInfo || contactBranding;
  const tooltipColors = getTooltipColors(finalContactBranding);

  const filterForProposals = {
    [ProposalTypes.KeyPersonInsurance]: ProposalFilter,
    [ProposalTypes.BuySell]: ProposalFilter,
    [ProposalTypes.SplitDollar]: FundingSDFilters,
    [ProposalTypes.PremiumFinance]: FundingPFFilters,
  };

  const insuredDetails = proposalSummary?.insuredDetails?.[0] ?? proposalSummary?.primaryContact;

  const calculateChartData = useCallback(
    (chartsData: any[], funding: string[], brandingColor: string) => {
      const datasets = [];
      if (proposalSummary?.proposalType === ProposalTypes.PremiumFinance) {
        if (funding.includes(FundingPFFilters.CLIENT_PREMIUM)) {
          datasets.push({
            name: "Premium",
            type: "bar",
            data: chartsData?.map((item: any) => item?.clientPremium),
            itemStyle: {
              color: fundingPFOptions(brandingColor)[0].indicatorColor,
            },
          });
        }
        if (funding.includes(FundingPFFilters.FINANCED_PREMIUM)) {
          datasets.push({
            name: "Financed Premium",
            type: "bar",
            data: chartsData?.map((item: any) => item?.lenderPremium),
            itemStyle: {
              color: fundingPFOptions(brandingColor)[1].indicatorColor,
            },
          });
        }
        if (funding.includes(FundingPFFilters.EMPLOYEE_INTEREST_PAYMENT)) {
          datasets.push({
            name: "Interest Payment",
            type: "bar",
            data: chartsData?.map((item: any) => item?.clientInterestPayment),
            itemStyle: {
              color: fundingPFOptions(brandingColor)[2].indicatorColor,
            },
          });
        }
        if (funding.includes(FundingPFFilters.ADMIN_FEES)) {
          datasets.push({
            name: "Plan Fees",
            type: "bar",
            data: chartsData?.map((item: any) => Math.round(item?.clientPlanFee)),
            itemStyle: {
              color: fundingPFOptions(brandingColor)[3].indicatorColor,
            },
          });
        }

        if (funding.includes(FundingPFFilters.PLAN_ORIGINATION_FEES)) {
          datasets.push({
            name: "Origination Fees",
            type: "bar",
            data: chartsData?.map((item: any) => item?.originationFees),
            itemStyle: {
              color: fundingPFOptions(brandingColor)[4].indicatorColor,
            },
          });
        }
      } else if (proposalSummary?.proposalType === ProposalTypes.SplitDollar) {
        if (funding.includes(FundingSDFilters.EMPLOYEE_PREMIUM)) {
          datasets.push({
            name: "Employee Premium",
            type: "bar",
            data: chartsData?.map((item: any) => item?.employeePremium),
            itemStyle: {
              color: fundingSDOptions(brandingColor)[0].indicatorColor,
            },
          });
        }
        if (funding.includes(FundingSDFilters.EMPLOYER_PREMIUM)) {
          datasets.push({
            name: "Employer Premium",
            type: "bar",
            data: chartsData?.map((item: any) => item?.employerPremium),
            itemStyle: {
              color: fundingSDOptions(brandingColor)[1].indicatorColor,
            },
          });
        }
        if (funding.includes(FundingSDFilters.EMPLOYEE_INTEREST_PAYMENT)) {
          datasets.push({
            name: "Interest Payment",
            type: "bar",
            data: chartsData?.map((item: any) => item?.employeeInterestPayment),
            itemStyle: {
              color: fundingSDOptions(brandingColor)[2].indicatorColor,
            },
          });
        }
        if (funding.includes(FundingSDFilters.ADMIN_FEES)) {
          datasets.push({
            name: "Plan Fees",
            type: "bar",
            data: chartsData?.map((item: any) => item?.adminFees),
            itemStyle: {
              color: fundingSDOptions(brandingColor)[3].indicatorColor,
            },
          });
        }
        if (funding.includes(FundingSDFilters.PLAN_ORIGINATION_FEES)) {
          datasets.push({
            name: "Origination Fees",
            type: "bar",
            data: chartsData?.map((item: any) => item?.planOriginationFees),
            itemStyle: {
              color: fundingSDOptions(brandingColor)[4].indicatorColor,
            },
          });
        }
      } else {
        if (funding.includes(ProposalFilter.LIFE)) {
          datasets.push({
            name: "Life Premium",
            type: "bar",
            data: chartsData?.map((item: any) => item?.lifePolicyPremium),
            itemStyle: {
              color: fundingOptions(brandingColor)[0].indicatorColor,
            },
          });
        }
        if (funding.includes(ProposalFilter.DISABILITY)) {
          datasets.push({
            name: "Disability Premium",
            type: "bar",
            data: chartsData?.map((item: any) => item?.disabilityPolicyPremium),
            itemStyle: {
              color: fundingOptions(brandingColor)[1].indicatorColor,
            },
          });
        }
        if (funding.includes(ProposalFilter.ADMIN_FEES)) {
          datasets.push({
            name: "Plan Fees",
            type: "bar",
            data: chartsData?.map((item: any) => item?.adminFees),
            itemStyle: {
              color: fundingOptions(brandingColor)[2].indicatorColor,
            },
          });
        }
        if (funding.includes(ProposalFilter.PLAN_ORIGINATION_FEES)) {
          datasets.push({
            name: "Origination Fees",
            type: "bar",
            data: chartsData?.map((item: any) => item?.planOriginationFees),
            itemStyle: {
              color: fundingOptions(brandingColor)[3].indicatorColor,
            },
          });
        }
      }
      return datasets;
    },
    [],
  );

  // Define the header structure headerColumnGroupProjection

  const kpFundingTableClassName = `
    .insured-table .p-datatable-thead > tr:nth-child(1) > th:not(.p-sortable-column.p-highlight) {
      background: ${globalTheme === THEME.DARK ? "#2A2A3D" : "#F7F7F8"} !important;
    }
  `;
  const headerColumnGroupEle = (
    <Row>
      <Column
        header=""
        rowSpan={1}
        colSpan={proposalSummary?.proposalType === ProposalTypes.SplitDollar ? 1 : 3}
        style={{ background: globalTheme === THEME.DARK ? "#2A2A3D" : "#EBEBEF" }}
        // style={{
        //   borderRightWidth: 1,
        //   borderRightColor: "var(--surface-100)",
        //   borderBottomWidth: 1,
        //   borderBottomColor: "var(--surface-100)",
        // }}
        align="center"
      />
      {(
        Object.keys(headerColumnGroupFundingSD) as Array<keyof typeof headerColumnGroupFundingSD>
      ).map((key: any) => (
        <Column
          header={key}
          key={key}
          rowSpan={1}
          colSpan={
            headerColumnGroupFundingSD[key as keyof typeof headerColumnGroupFundingSD].length
          }
          style={{ background: globalTheme === THEME.DARK ? "#2A2A3D" : "#EBEBEF" }}
          // style={{
          //   borderRightWidth: 1,
          //   borderRightColor: "var(--surface-100)",
          //   borderBottomWidth: 1,
          //   borderBottomColor: "var(--surface-100)",
          // }}
          align="center"
        />
      ))}
    </Row>
  );

  const headerGroupFundingPF = (
    <Row>
      <Column
        header=""
        rowSpan={1}
        colSpan={
          proposalSummary?.proposalType === ProposalTypes.PremiumFinance
            ? selectedChartFilter === "year"
              ? 1
              : 2
            : 3
        }
        style={{ background: globalTheme === THEME.DARK ? "#3A3A50" : "#EBEBEF" }}
        // style={{
        //   borderRightWidth: 1,
        //   borderRightColor: "var(--surface-100)",
        //   borderBottomWidth: 1,
        //   borderBottomColor: "var(--surface-100)",
        // }}
        align="center"
      />
      {(
        Object.keys(headerColumnGroupFundingPF) as Array<keyof typeof headerColumnGroupFundingPF>
      ).map((key: any) => (
        <Column
          header={key}
          key={key}
          rowSpan={1}
          colSpan={
            headerColumnGroupFundingPF[key as keyof typeof headerColumnGroupFundingPF].length
          }
          style={{
            background: globalTheme === THEME.DARK ? "#3A3A50" : "#EBEBEF",
          }}
          // style={{
          //   borderRightWidth: 1,
          //   borderRightColor: "var(--surface-100)",
          //   borderBottomWidth: 1,
          //   borderBottomColor: "var(--surface-100)",
          // }}
          align="center"
        />
      ))}
    </Row>
  );
  const onShareholdersChange = useCallback(
    async (
      selectedShareholderIds: string[],
      selectedFundingIds: string[],
      brandingColor: string,
    ) => {
      selectedFundingData = selectedFundingIds;
      let newShareholderIds = selectedShareholderIds;
      if (proposalSummary?.proposalType === ProposalTypes.SplitDollar) {
        newShareholderIds = [];
      }
      setLoading(true);
      setIsCurrPageLoading(true);
      const response = await makeRequest("interactiveProposalFunding").get(
        `${proposalId}?insuredIds=${JSON.stringify(newShareholderIds)}&filter=${JSON.stringify(
          selectedFundingIds,
        )}`,
        true,
        false,
      );
      setIsCurrPageLoading(false, true);
      if (response.status === 200) {
        const data = calculateChartData(
          response?.data?.data?.graphData,
          selectedFundingIds,
          brandingColor,
        );
        if (
          selectedShareholderIds.length === 0 &&
          proposalSummary?.proposalType === ProposalTypes.SplitDollar
        ) {
          setChartDataSets(null);
        } else {
          setChartDataSets(data);
        }
        setGraphData(response?.data?.data?.graphData);

        if (proposalSummary?.proposalType === ProposalTypes.SplitDollar) {
          const tableDataUpdated = response?.data?.data?.tableData.map((item: any) => ({
            ...item,
            planYear: item?.policyYear,
            year: item?.planYear,
          }));
          setTableData(tableDataUpdated || []);
        } else {
          setTableData(response?.data?.data?.tableData || []);
        }
      }
      setLoading(false);
    },
    [proposalId],
  );

  const accordionHeader = (title: string) => (
    <div className="text-lg conthrax-font ml-2 text-color-black">{title}</div>
  );

  const collapseIcon = (
    <div className="rounded-full bg-[#AEB0C6] p-[1px] cursor-pointer">
      <GoTriangleDown
        className="text-[#8A8AA3]/70"
        size={27}
        style={{ transition: "transform 0.2s" }}
      />
    </div>
  );
  const expandIcon = (
    <div className="rounded-full bg-[#AEB0C6] p-[1px] cursor-pointer">
      <GoTriangleUp
        className="text-[#8A8AA3]/70"
        size={27}
        style={{ transition: "transform 0.2s", rotate: "140deg" }}
      />
    </div>
  );

  const getChartLabels = () => {
    if (selectedChartFilter === "insuredAge") {
      return graphData?.map((item: any) => `A${item?.insuredAge}`);
    }
    if (selectedChartFilter === "planYear") {
      return graphData?.map((item: any) => item?.planYear);
    }
    return graphData?.map((item: any) => {
      const curlyApostrophe = "\u2019"; // ’
      // If year is a number like 2025, return last two digits as string
      if (typeof item?.year === "number") {
        return curlyApostrophe + String(item.year).slice(-2);
      }
      // If year is a string, also slice last two digits
      if (typeof item?.year === "string") {
        return curlyApostrophe + item.year.slice(-2);
      }
      return item?.year;
    });
  };

  const color = isDarkMode ? "#ffffff" : "#333333";

  const createTooltipHTML = (params: any) => {
    // Get current data point for dynamic values
    const currentData = graphData?.[params[0]?.dataIndex];
    const year = currentData?.year || 2025;
    const age = currentData?.insuredAge;
    const planYear = currentData?.planYear || 1;

    const header = `<div style="
      background: ${isDarkMode ? "#3A3A50" : "#E5E7EB"};
      color: ${isDarkMode ? color : "#374151"};
      padding: 8px 12px;
      text-align: center;
      font-weight: bold;
      font-family: 'Conthrax Semi-Bold', 'Arial', sans-serif;
      border-bottom: 1px solid ${isDarkMode ? "#4A4A60" : "#e0e0e0"};
      border-radius: 8px 8px 0 0;
      display: flex;
      justify-content: center;
      align-items: center;
      gap: 30px;
    ">
      <div style="display: flex; align-items: center; gap: 6px;">
        <i class="pi pi-calendar" style="font-size: 14px; color: ${
          isDarkMode ? "#ffffff" : "#1F2937"
        }; font-weight: bold;"></i>
        <span style="font-weight: bold; font-size: 16px">${year}</span>
      </div>
      <div style="width: 1px; height: 20px; background: ${
        isDarkMode ? "#ffffff" : "#9CA3AF"
      }; opacity: 0.4;"></div>
      <div style="display: flex; align-items: center; gap: 6px;">
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" style="color: ${
          isDarkMode ? "#ffffff" : "#1F2937"
        }; transform: translateY(1px);">
          <rect x="4" y="12" width="16" height="3" stroke="currentColor" stroke-width="1.5" fill="none"/>
          <rect x="6" y="8" width="12" height="4" stroke="currentColor" stroke-width="1.5" fill="none"/>
          <rect x="8" y="4" width="8" height="4" stroke="currentColor" stroke-width="1.5" fill="none"/>
          <line x1="12" y1="2" x2="12" y2="4" stroke="currentColor" stroke-width="1.5"/>
          <path d="M11 2L12 1L13 2" stroke="currentColor" stroke-width="1.5" fill="none"/>
        </svg>
        <span style="font-weight: bold; font-size: 16px">${planYear}</span>
      </div>
      <div style="width: 1px; height: 20px; background: ${
        isDarkMode ? "#ffffff" : "#9CA3AF"
      }; opacity: 0.4;"></div>
      ${
        age
          ? `<div style="display: flex; align-items: center; gap: 6px;">
        <i class="pi pi-user" style="font-size: 14px; color: ${
          isDarkMode ? "#ffffff" : "#1F2937"
        }; font-weight: bold;"></i>
        <span style="font-weight: bold; font-size: 16px">A${age}</span>
      </div>`
          : ""
      }
    </div>`;

    let content = '<div style="padding: 8px 12px;">';

    params.forEach((param: any) => {
      if (param.value !== null && param.value !== undefined) {
        const bulletColor = param.color || "#4A90E2";
        const isHighlighted = param.seriesName === selectedSeriesRef.current;

        content += `<div style="
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 4px 8px;
          margin: 2px 0;
          border-bottom: 1px solid ${isDarkMode ? "#4A4A60" : "#E5E7EB"};
          ${isHighlighted ? `background: ${tooltipColors.primaryColor}20; border-radius: 4px;` : ""}
        ">
          <div style="display: flex; align-items: center; gap: 8px;">
            <div style="
              width: 8px;
              height: 8px;
              border-radius: 50%;
              background-color: ${isHighlighted ? tooltipColors.secondaryColor : bulletColor};
              flex-shrink: 0;
            "></div>
            <span style="
              color: ${
                isHighlighted ? tooltipColors.secondaryColor : isDarkMode ? "#ffffff" : "#374151"
              };
              font-family: ${
                isHighlighted ? "'Conthrax Semi-Bold', 'Arial', sans-serif" : "inherit"
              };
              font-size: ${isHighlighted ? "14px" : "12px"};
              font-weight: ${isHighlighted ? "bold" : "normal"};
            ">${param.seriesName}</span>
          </div>
          <span style="
            color: ${
              isHighlighted ? tooltipColors.secondaryColor : isDarkMode ? "#ffffff" : "#374151"
            };
            margin-left: 12px;
            font-family: ${isHighlighted ? "'Conthrax Semi-Bold', 'Arial', sans-serif" : "inherit"};
            font-size: ${isHighlighted ? "14px" : "12px"};
            font-weight: ${isHighlighted ? "bold" : "normal"};
            display: inline-block;
            text-align: left;
            min-width: 80px;
            flex-shrink: 0;
          ">$${getUSANumberFormat(param.value, false)}</span>
        </div>`;
      }
    });

    content += "</div>";
    return `<div style="
      background: ${isDarkMode ? "#2A2A3D" : "#ffffff"};
      border: 1px solid ${isDarkMode ? "#3A3A50" : "#E5E7EB"};
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      overflow: hidden;
    ">${header}${content}</div>`;
  };
  const mainContent = () => {
    if (loading) {
      return <ScreenLoader />;
    }
    return (
      <div>
        <Accordion
          activeIndex={[0, 1]}
          collapseIcon={collapseIcon}
          expandIcon={expandIcon}
          multiple
          className="bg-gray-100 dark:!bg-[#111111] rounded-md  ip-project-accordion-main"
        >
          <AccordionTab
            headerTemplate={accordionHeader("Funding Chart")}
            headerClassName="interactive-proposal-projection"
            className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
          >
            <CommentLayout commentLayoutId="interactive-proposal-funding-chart">
              {chartDataSets ? (
                <div
                  style={{ width: "100%", height: "365px", margin: 0, padding: 0 }}
                  className="w-full"
                >
                  <ReactECharts
                    onEvents={{
                      click: (params: any) => {
                        selectedSeriesRef.current = params?.seriesName || null;
                      },
                      mouseover: (params: any) => {
                        selectedSeriesRef.current = params?.seriesName || null;
                      },
                      mouseout: () => {
                        selectedSeriesRef.current = null;
                      },
                      dataZoom: (params: any) => {
                        zoomStateRef.current = { start: params.start, end: params.end };
                      },
                    }}
                    option={{
                      title: {
                        text:
                          proposalSummary?.proposalType === ProposalTypes.PremiumFinance
                            ? "Plan Funding for Insured Persons"
                            : proposalSummary?.proposalType === ProposalTypes.KeyPersonInsurance
                            ? "Plan Funding for Selected Employees"
                            : proposalSummary?.proposalType === ProposalTypes.SplitDollar
                            ? `Plan Funding for ${insuredDetails.firstName} ${insuredDetails.lastName}`
                            : "Plan Funding for Selected Shareholders",
                        bottom: 40,
                        textStyle: {
                          color,
                          fontSize: 14,
                        },
                      },
                      animation: false,
                      throttle: 70,
                      tooltip: {
                        trigger: "axis",
                        axisPointer: {
                          type: "none",
                        },
                        backgroundColor: isDarkMode ? "#2A2A3D" : "#ffffff",
                        borderColor: isDarkMode ? "#3A3A50" : "#e0e0e0",
                        borderWidth: 1,
                        borderRadius: 8,
                        padding: 0,
                        textStyle: {
                          color,
                          fontSize: 12,
                        },
                        formatter(params: any) {
                          if (!params || params.length === 0) return "";
                          // const year = params[0].axisValue;
                          // const yearLabel = `Year ${year}`;

                          return createTooltipHTML(params);
                        },
                        extraCssText: `
                        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
                        border: none !important;
                      `,
                      },
                      legend: {
                        show: false,
                      },
                      grid: {
                        left: "3%",
                        right: "3%",
                        bottom: "25%",
                        top: "5%",
                        containLabel: true,
                        show: false,
                      },
                      dataZoom: [
                        {
                          type: "slider",
                          show: true,
                          start: zoomStateRef.current?.start || 0,
                          end:
                            zoomStateRef.current?.end ||
                            Math.min(100, (10 / (graphData?.length || 1)) * 100),
                          minValueSpan: 1,
                          maxValueSpan: graphData?.length || 1,
                          height: 30,
                          bottom: 5,
                          borderColor: "transparent",
                          fillerColor: isDarkMode
                            ? "rgba(167,183,204,0.3)"
                            : "rgba(167,183,204,0.4)",
                          handleStyle: {
                            color: isDarkMode ? "#ffffff" : "#333333",
                            borderColor: isDarkMode ? "#4A4A60" : "#E5E7EB",
                          },
                          textStyle: {
                            color,
                            fontSize: 10,
                          },
                          showDetail: true,
                          showDataShadow: true,
                          realtime: true,
                          rangeMode: ["percent", "percent"],
                          filterMode: "filter",
                          xAxisIndex: 0,
                        },
                      ],
                      xAxis: {
                        type: "category",
                        data: getChartLabels(),
                        axisLabel: {
                          color,
                          fontFamily: "Conthrax Semi-Bold",
                          fontSize: 10,
                        },
                        axisLine: {
                          show: false,
                          lineStyle: {
                            color,
                          },
                        },
                      },
                      yAxis: {
                        type: "value",
                        name: "Amount",
                        nameLocation: "middle",
                        nameGap: 60,
                        nameTextStyle: {
                          color,
                          fontSize: 12,
                        },
                        splitLine: {
                          show: false,
                        },
                        axisLabel: {
                          color,
                          formatter: (value: number) => `$${intToString(value)}`,
                          fontFamily: "Conthrax Semi-Bold",
                          fontSize: 10,
                        },
                        axisLine: {
                          show: true,
                          lineStyle: {
                            color,
                          },
                        },
                        max(value: any) {
                          return Math.round(value.max * 1.2);
                        },
                        min: 0,
                      },
                      series: chartDataSets.map((dataset: any) => ({
                        name: dataset.name,
                        type: dataset.type,
                        data: dataset.data,
                        stack: "all",
                        itemStyle: dataset.itemStyle,
                        barWidth: "60%",
                        barGap: "10%",
                      })),
                    }}
                    style={{ width: "100%", height: "100%", margin: 0, padding: 0 }}
                    notMerge
                    lazyUpdate
                  />
                </div>
              ) : (
                <div className="flex justify-center items-center h-full">
                  <p className="text-lg text-center">No data available</p>
                </div>
              )}
              <div className="mt-2">
                <ChartFilter />
              </div>
            </CommentLayout>
          </AccordionTab>
          <AccordionTab
            headerTemplate={accordionHeader("Funding Table")}
            headerClassName="interactive-proposal-projection"
            className="bg-light-primary dark:bg-[#2A2A3D] rounded-md"
          >
            {/* Funding Table */}
            <CommentLayout
              commentLayoutId="interactive-proposal-funding-table"
              className="flex justify-between w-full mt-1"
            >
              {/* Table code here */}
              {!loading && (
                <div className="w-full">
                  <InsuredTable
                    columns={
                      proposalSummary?.proposalType === ProposalTypes.PremiumFinance
                        ? fundingPFColumnJson(selectedFundingData, selectedChartFilter)
                        : proposalSummary?.proposalType === ProposalTypes.SplitDollar
                        ? fundingSDColumnJson(selectedFundingData, selectedChartFilter)
                        : fundingColumnJson(selectedFundingData, selectedChartFilter)
                    }
                    data={tableData}
                    hoverColor={isDarkMode ? secondaryColor : primaryColor}
                    columnGroup={
                      proposalSummary?.proposalType === ProposalTypes.PremiumFinance
                        ? headerGroupFundingPF
                        : proposalSummary?.proposalType === ProposalTypes.SplitDollar
                        ? headerColumnGroupEle
                        : null
                    }
                    scrollable
                    scrollHeight="600px"
                    className={
                      proposalSummary?.proposalType === ProposalTypes.KeyPersonInsurance ||
                      proposalSummary?.proposalType === ProposalTypes.BuySell
                        ? kpFundingTableClassName
                        : ""
                    }
                  />
                  <p className="text-lg text-center my-5">
                    {proposalSummary?.proposalType === ProposalTypes.PremiumFinance
                      ? "Plan Funding for Insured Persons"
                      : proposalSummary?.proposalType === ProposalTypes.SplitDollar
                      ? `Plan Funding for ${insuredDetails.firstName} ${insuredDetails.lastName}`
                      : "Plan Funding for Selected Shareholders"}
                  </p>
                </div>
              )}
            </CommentLayout>
          </AccordionTab>
        </Accordion>
      </div>
    );
  };

  return (
    <CommentLayout
      commentLayoutId="interactive-proposal-funding"
      className={`${
        !loading ? "rounded-2xl" : "rounded-t-2xl rounded-b-md"
      }  w-full shadow-lg bg-gray-100 dark:!bg-[#111111] `}
    >
      {loading && <ScreenLoader />}
      {/* Filter container */}
      <div
        className={`p-3 flex justify-between shrink-0 w-ful ${
          !loading ? "mb-1 rounded-t-md rounded-b-md" : "mb-1"
        } border-b-2 border-gray-300 dark:!border-[#3A3A50] bg-light-primary dark:bg-[#2A2A3D] `}
      >
        <InsuredFilter
          onShareholdersChange={onShareholdersChange}
          brandingColor={primaryColor}
          label="Funding"
          filter={
            filterForProposals[proposalSummary?.proposalType as keyof typeof filterForProposals]
          }
        />
      </div>
      {/* Content container */}
      <div>{mainContent()}</div>
      <div className="hidden">{JSON.stringify(settingThemeData)}</div>
    </CommentLayout>
  );
};
export default InteractiveProposalFunding;
